    <template>
    <div class="quantity-unit-input">
        <div class="d-flex">
            <div class="flex-grow-1" style="min-width: 120px;">
                <v-text-field
                    ref="input"
                    :autofocus="autofocus"
                    :disabled="disabled"
                    :error-messages="errorMessages"
                    :filled="filled"
                    :key="inputKey"
                    :label="label"
                    :outlined="outlined"
                    :placeholder="placeholder"
                    :prepend-inner-icon="prependInnerIcon"
                    :required="required"
                    :suffix="displayedUnitSuffix"
                    :value="displayQuantity"
                    @input="updateQuantity"
                    @blur="onBlur"
                    @keydown="$emit('keydown', $event)"
                    class="quantity-unit-input__field"
                    dense
                    hide-details="auto"
                    type="text"
                    :inputmode="allowNegative ? 'text' : 'decimal'"
                    autocomplete="off"
                >
                    <template v-if="$scopedSlots.label" #label>
                        <slot name="label" />
                    </template>
                </v-text-field>
            </div>
            <div
                v-if="shouldShowUnitSelector"
                class="flex-grow-0 ml-2 quantity-unit-input__unit"
                :style="unitSelectorStyle"
            >
                <QuantityUnitSelect
                    v-if="hasFormatUnitOptions"
                    :disabled="disabled"
                    :filled="filled"
                    :outlined="outlined"
                    :options="unitOptions"
                    :value="selectedUnit"
                    @input="updateUnit"
                    @keydown="$emit('keydown', $event)"
                    class="quantity-unit-input__unit-select"
                />
                <v-select
                    v-else
                    :disabled="disabled"
                    :filled="filled"
                    :items="unitOptions"
                    :outlined="outlined"
                    :value="selectedUnit"
                    :menu-props="{ maxWidth: unitSelectorMenuWidth }"
                    @input="updateUnit"
                    @keydown="$emit('keydown', $event)"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details
                    class="quantity-unit-input__unit-select"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Decimal from "decimal.js";
import QuantityUnitSelect from "@/components/common/quantity-unit-select.vue";
import {
    formatDecimalForInput,
    sanitizeDecimalInputString,
} from "@/utils/numberFormat";

export default {
    name: 'QuantityUnitInput',
    components: { QuantityUnitSelect },
    props: {
        prependInnerIcon: { type: String, default: undefined },
        // Either a plain quantity (Number/String) or a { quantity, unit }
        // object; the emitted value mirrors the input shape.
        value: {
            type: [Number, String, Object],
            default: 0
        },
        unitType: {
            type: String,
            default: 'piece',
            validator: value => ['weight', 'volume', 'piece'].includes(value)
        },
        label: {
            type: String,
            default: ''
        },
        errorMessages: {
            type: Array,
            default: () => []
        },
        required: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: ''
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        allowedUnits: {
            type: Array,
            default: null,
            validator: value => !value || Array.isArray(value)
        },
        unitOptionItems: {
            type: Array,
            default: null,
            validator: value => !value || Array.isArray(value),
        },
        preserveInputUnit: {
            type: Boolean,
            default: false,
        },
        allowDecimalQuantity: {
            type: Boolean,
            default: false,
        },
        allowNegative: {
            type: Boolean,
            default: false,
        },
        outlined: {
            type: Boolean,
            default: false
        },
        filled: {
            type: Boolean,
            default: false
        },
        min: {
            type: Number,
            default: 0
        },
        // Static unit label (e.g. a sale unit label like "caisse") shown as
        // input suffix when the unit selector is hidden.
        unitSuffix: {
            type: String,
            default: null
        },
        hideUnitSelector: {
            type: Boolean,
            default: false,
        },
        product: {
            type: Object,
            default: null,
        },

    },
    data() {
        return {
            userSelectedUnit: null, // Track the user-selected unit
            inputKey: 0, // Key to force input field reset
            lastDisplayValue: null, // Keep track of the last displayed value before unit change
            editingText: null, // Raw text while the user is typing (keeps partial input like "1,")
        };
    },
    computed: {
        isObjectValue() {
            return this.value !== null && typeof this.value === 'object'
                && !(this.value instanceof Decimal);
        },
        quantityValue() {
            return this.isObjectValue ? this.value?.quantity : this.value;
        },
        unitOptions() {
            if (this.unitOptionItems && this.unitOptionItems.length > 0) {
                return this.unitOptionItems;
            }
            if (this.allowedUnits && this.allowedUnits.length > 0) {
                return this.allowedUnits.map(unit => ({
                    text: unit,
                    value: unit
                }));
            }
            return this.$utils.getShortUnitOptions(this.unitType);
        },
        selectedUnit() {
            // Use user-selected unit if available, otherwise determine from value
            return this.userSelectedUnit || this.getUnitFromValue();
        },
        displayQuantity() {
            // Raw text while typing (keeps partial input like "1,")
            if (this.editingText !== null) {
                return this.editingText;
            }
            // If we have a lastDisplayValue stored (from unit change without user input), use it
            if (this.lastDisplayValue !== null) {
                return this.lastDisplayValue;
            }
            return this.getDisplayQuantity();
        },
        hasFormatUnitOptions() {
            // Rich options carrying format data switch to the cascade
            // selector (container -> capacity) instead of a flat list.
            return this.unitOptions.some((option) => option.format);
        },
        shouldShowUnitSelector() {
            if (this.hideUnitSelector) {
                return false;
            }
            if (this.unitOptionItems && this.unitOptionItems.length > 1) {
                return true;
            }
            if (this.allowedUnits && this.allowedUnits.length > 1) {
                return true;
            }
            return this.unitType !== 'piece';
        },
        displayedUnitSuffix() {
            // The selector already shows the unit; avoid displaying it twice.
            if (this.shouldShowUnitSelector) {
                return null;
            }
            return this.unitSuffix || null;
        },
        unitSelectorStyle() {
            const minWidth = 80;
            const maxWidth = 260;
            const longestLabel = Math.max(
                2,
                ...this.unitOptions.map((option) => String(option.text || '').length),
            );
            const width = Math.min(Math.max(longestLabel * 9 + 48, minWidth), maxWidth);
            return {
                width: `${width}px`,
                minWidth: `${minWidth}px`,
                maxWidth: `${maxWidth}px`,
            };
        },
        unitSelectorMenuWidth() {
            const longestLabel = Math.max(
                2,
                ...this.unitOptions.map((option) => String(option.text || '').length),
            );
            return Math.min(Math.max(longestLabel * 9 + 48, 120), 320);
        },
    },
    methods: {
        focus() {
            this.$refs.input?.focus();
        },
        isCustomQuantityUnit(unit) {
            return typeof unit === 'string'
                && (unit.startsWith('sf:')
                    || unit.startsWith('ce:')
                    || unit === 'sale_pc');
        },
        usesRawQuantityUnit() {
            return this.preserveInputUnit || this.isCustomQuantityUnit(this.selectedUnit);
        },
        allowDecimalForSelectedUnit() {
            const unit = this.selectedUnit;
            if (unit === 'sale_pc' || this.isCustomQuantityUnit(unit)) {
                if (this.preserveInputUnit) {
                    return this.allowDecimalQuantity;
                }
                return true;
            }
            if (unit === 'pc') {
                return this.allowDecimalQuantity;
            }
            return this.allowDecimalQuantity || this.unitType !== 'piece';
        },
        emitQuantity(quantity) {
            if (this.isObjectValue) {
                this.$emit('input', {
                    quantity,
                    unit: this.selectedUnit,
                });
                return;
            }
            this.$emit('input', quantity);
        },
        getUnitFromValue() {
            const allowedValues = this.unitOptions.map((option) => option.value);
            // Explicit unit from an object value takes precedence when valid
            if (this.isObjectValue && this.value?.unit) {
                const unit = this.value.unit;
                if (!allowedValues.length || allowedValues.includes(unit)) {
                    return unit;
                }
            }
            if (allowedValues.length) {
                return allowedValues[0];
            }
            // Value is stored in kg/L (not g/ml)
            // Determine best display unit based on stored value
            if (this.unitType === 'piece') {
                return 'pc';
            }

            const storedValue = new Decimal(this.quantityValue || 0);

            // If allowedUnits is specified
            if (this.allowedUnits && this.allowedUnits.length > 0) {
                if (this.allowedUnits.includes('kg') && this.allowedUnits.includes('g')) {
                    // Weight: show g if < 1kg, otherwise kg
                    return storedValue.abs().lt(1) && storedValue.abs().gt(0) ? 'g' : 'kg';
                } else if (this.allowedUnits.includes('L') && this.allowedUnits.includes('ml')) {
                    // Volume: show ml if < 0.01L, cl if < 1L, otherwise L
                    if (storedValue.abs().lt(0.01) && storedValue.abs().gt(0)) {
                        return 'ml';
                    } else if (storedValue.abs().lt(1)) {
                        return 'cl';
                    }
                    return 'L';
                }
                return this.allowedUnits[0];
            }

            // Default behavior for unitType
            if (this.unitType === 'weight') {
                // Show g if < 1kg, otherwise kg
                return storedValue.abs().lt(1) && storedValue.abs().gt(0) ? 'g' : 'kg';
            } else if (this.unitType === 'volume') {
                // Show ml if < 0.01L, cl if < 1L, otherwise L
                if (storedValue.abs().lt(0.01) && storedValue.abs().gt(0)) {
                    return 'ml';
                } else if (storedValue.abs().lt(1)) {
                    return 'cl';
                }
                return 'L';
            }

            return 'pc';
        },
        formatForDisplay(decimalValue) {
            return formatDecimalForInput(decimalValue.toNumber());
        },
        getDisplayQuantity() {
            // Value is stored in kg/L, convert to display unit (g/ml/cl if needed)
            const quantity = this.quantityValue;
            if (quantity === null || quantity === undefined || quantity === '') {
                return '';
            }

            if (this.usesRawQuantityUnit()) {
                return this.formatForDisplay(new Decimal(quantity));
            }

            const storedValue = new Decimal(quantity);
            const currentUnit = this.selectedUnit;

            // If allowedUnits is specified
            if (this.allowedUnits && this.allowedUnits.length > 0) {
                if (this.allowedUnits.includes('kg') && this.allowedUnits.includes('g')) {
                    // Weight: stored in kg
                    if (currentUnit === 'kg') {
                        return this.formatForDisplay(storedValue);
                    } else {
                        // Convert kg to g
                        return this.formatForDisplay(storedValue.mul(1000));
                    }
                } else if (this.allowedUnits.includes('L') && this.allowedUnits.includes('ml')) {
                    // Volume: stored in L
                    if (currentUnit === 'L') {
                        return this.formatForDisplay(storedValue);
                    } else if (currentUnit === 'cl') {
                        return this.formatForDisplay(storedValue.mul(100));
                    } else {
                        // Convert L to ml
                        return this.formatForDisplay(storedValue.mul(1000));
                    }
                } else {
                    return this.formatForDisplay(storedValue);
                }
            }

            // Default behavior for unitType
            if (this.unitType === 'weight') {
                // Stored in kg
                if (currentUnit === 'kg') {
                    return this.formatForDisplay(storedValue);
                } else {
                    // Convert kg to g
                    return this.formatForDisplay(storedValue.mul(1000));
                }
            } else if (this.unitType === 'volume') {
                // Stored in L
                if (currentUnit === 'L') {
                    return this.formatForDisplay(storedValue);
                } else if (currentUnit === 'cl') {
                    return this.formatForDisplay(storedValue.mul(100));
                } else {
                    // Convert L to ml
                    return this.formatForDisplay(storedValue.mul(1000));
                }
            }

            return this.formatForDisplay(storedValue);
        },
        isTransientNumericInput(value) {
            // Partial input that can still become a valid number as the user
            // keeps typing (e.g. "-", "0", "0,", "-0,5" while aiming for a
            // longer value). Anything else is genuinely invalid.
            if (typeof value !== 'string') {
                return false;
            }
            const pattern = this.allowNegative
                ? /^-?\d*(?:[.,]\d*)?$/
                : /^\d*(?:[.,]\d*)?$/;
            return pattern.test(value.trim());
        },
        parseInputText(value) {
            // Accept both comma and point as decimal separator
            const sanitized = sanitizeDecimalInputString(value, {
                maxDecimals: 6,
                allowNegative: this.allowNegative,
            }).replace(/\.$/, "");
            if (sanitized === '' || sanitized === '-') {
                return null;
            }
            try {
                const parsed = new Decimal(sanitized);
                return parsed.isNaN() ? null : parsed;
            } catch (e) {
                return null;
            }
        },
        updateQuantity(value) {
            // Clear lastDisplayValue when user starts typing
            this.lastDisplayValue = null;
            this.editingText = typeof value === 'string' ? value : null;

            // Convert from display unit back to storage unit (kg/L)
            if (value === '' || value === null || value === undefined) {
                this.editingText = null;
                this.emitQuantity(null);
                return;
            }

            const inputQuantity = this.parseInputText(value);
            if (inputQuantity === null) {
                // Keep transient input like "-" or "0," so the user can
                // finish typing; only hard-reset genuinely invalid text.
                if (this.isTransientNumericInput(value)) {
                    this.emitQuantity(null);
                    return;
                }
                this.resetInputField();
                return;
            }

            // First, reject negative values unless allowed
            if (!this.allowNegative && inputQuantity.lt(0)) {
                this.resetInputField();
                return;
            }
            if (this.allowNegative && inputQuantity.isZero()) {
                // Transient state while typing e.g. "0,5" or "-0,5": keep the
                // text and emit null; consumers filter out empty quantities.
                this.emitQuantity(null);
                return;
            }
            // Validation based on unit
            const unit = this.selectedUnit;
            if (this.isCustomQuantityUnit(unit) || unit === 'sale_pc') {
                if (this.allowDecimalForSelectedUnit()) {
                    const decimalPlaces = inputQuantity.toString().split('.')[1];
                    if (decimalPlaces && decimalPlaces.length > 3) {
                        this.resetInputField();
                        return;
                    }
                } else if (!inputQuantity.isInteger()) {
                    this.resetInputField();
                    return;
                }
            } else if (unit === 'pc' || this.unitType === 'piece') {
                if (this.allowDecimalQuantity) {
                    const decimalPlaces = inputQuantity.toString().split('.')[1];
                    if (decimalPlaces && decimalPlaces.length > 3) {
                        this.resetInputField();
                        return;
                    }
                } else if (!inputQuantity.isInteger()) {
                    this.resetInputField();
                    return;
                }
                // Decimal-allowed piece quantities may be below 1 (e.g. 0.5);
                // zero is let through so partial input like "0.5" can be typed,
                // and is filtered out by the consumers.
                if (
                    !this.allowNegative &&
                    !this.allowDecimalQuantity &&
                    inputQuantity.lt(1)
                ) {
                    this.resetInputField();
                    return;
                }
            } else if (unit === 'g' || unit === 'ml') {
                // Must be integer >= 1
                if (inputQuantity.lt(1) || !inputQuantity.isInteger()) {
                    this.resetInputField();
                    return;
                }
            } else if (unit === 'kg' || unit === 'L') {
                // Maximum 3 decimal places
                const decimalPlaces = inputQuantity.toString().split('.')[1];
                if (decimalPlaces && decimalPlaces.length > 3) {
                    this.resetInputField();
                    return;
                }
                // Minimum must be >= 0.001 (1g or 1ml)
                if (inputQuantity.abs().gt(0) && inputQuantity.abs().lt(0.001)) {
                    this.resetInputField();
                    return;
                }
            } else if (unit === 'cl') {
                // Maximum 3 decimal places
                const decimalPlaces = inputQuantity.toString().split('.')[1];
                if (decimalPlaces && decimalPlaces.length > 3) {
                    this.resetInputField();
                    return;
                }
                // Minimum must be >= 0.1 (1ml)
                if (inputQuantity.abs().gt(0) && inputQuantity.abs().lt(0.1)) {
                    this.resetInputField();
                    return;
                }
            }

            // Convert from display unit to storage unit (kg/L)
            let storedValue = inputQuantity;

            if (this.usesRawQuantityUnit()) {
                this.emitQuantity(inputQuantity.toDecimalPlaces(3).toString());
                return;
            }

            if (this.allowedUnits && this.allowedUnits.length > 0) {
                if (this.allowedUnits.includes('kg') && this.allowedUnits.includes('g')) {
                    // Weight: convert g to kg
                    if (unit === 'g') {
                        storedValue = inputQuantity.div(1000);
                    }
                    // If unit is kg, storedValue is already in kg
                } else if (this.allowedUnits.includes('L') && this.allowedUnits.includes('ml')) {
                    // Volume: convert ml/cl to L
                    if (unit === 'ml') {
                        storedValue = inputQuantity.div(1000);
                    } else if (unit === 'cl') {
                        storedValue = inputQuantity.div(100);
                    }
                    // If unit is L, storedValue is already in L
                }
            } else {
                // Default behavior for unitType
                if (this.unitType === 'weight') {
                    // Convert g to kg
                    if (unit === 'g') {
                        storedValue = inputQuantity.div(1000);
                    }
                    // If unit is kg, storedValue is already in kg
                } else if (this.unitType === 'volume') {
                    // Convert ml/cl to L
                    if (unit === 'ml') {
                        storedValue = inputQuantity.div(1000);
                    } else if (unit === 'cl') {
                        storedValue = inputQuantity.div(100);
                    }
                    // If unit is L, storedValue is already in L
                }
            }

            // Check minimum in storage unit (must be >= 0.001 kg/L which is 1g/ml)
            // But allow 0 for stock adjustments
            if (storedValue.eq(0)) {
                this.emitQuantity(0);
                return;
            }

            const minStorage = new Decimal('0.001');
            if (storedValue.abs().gt(0) && storedValue.abs().lt(minStorage)) {
                this.resetInputField();
                return;
            }
            this.emitQuantity(storedValue.toNumber());
        },
        resetInputField() {
            // Force input field reset by changing the key
            this.editingText = null;
            this.inputKey = Math.random();
        },
        onBlur(event) {
            // Reformat the field from the actual value once editing ends
            this.editingText = null;
            this.$emit('blur', event);
        },
        updateUnit(unit) {
            // Store the current display value before changing unit
            const currentDisplay = this.displayQuantity;
            this.lastDisplayValue = currentDisplay;

            // Update the internal unit selection
            this.userSelectedUnit = unit;
            this.updateQuantity(currentDisplay);

            // Keep lastDisplayValue to prevent auto-conversion
            // It will be cleared when user inputs a new value
        }
    }
};
</script>

<style scoped>
.quantity-unit-input {
    width: 100%;
}

.quantity-unit-input__field ::v-deep .v-text-field__suffix {
    font-size: 12px;
    line-height: 1.2;
}

.quantity-unit-input__unit {
    flex-shrink: 0;
}

.quantity-unit-input__unit-select ::v-deep .v-select__selections {
    flex-wrap: nowrap;
}

.quantity-unit-input__unit-select ::v-deep .v-select__selection--comma {
    white-space: nowrap;
}
</style>
